import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Photo } from '../_models/Photo';
import { S3Service } from './s3.service';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable, Subscriber, of, forkJoin, from } from 'rxjs';
import { map, mergeMap, tap, take, shareReplay } from 'rxjs/operators';
import { BaseService } from '../_shared/base.service';
import { getUrl } from '../_shared/utils';
import { JWTTokenService } from './jwtToken.service';
import { ImgResizeExactService } from './image-resize.service';
import { ImgMaxPXSizeService } from './img-maxpx-size.service';
import { Ng2ImgMaxService } from './ng2-img-max.service';
import { Ng2PicaService } from './ng2-pica.service';
import Jimp from 'jimp/browser/lib/jimp';
import { Environment } from '../../environments/environment';

export enum ImageProcessingStage {
  /** The image is in its original state in a temporary in-memory location */
  Original,
  /** The image resized and located in a temporary in-memory location*/
  Resized,
  /** The image resized and located on the s3 server*/
  Uploaded
}

@Injectable()
export class PhotoService extends BaseService {
  userToken: any;

  protected resizeObservablesDict: { [locations: string]: Observable<ImageProcessingInfo> } = {};
  protected pdfDict: any = {};
  protected tempUrls: string[] = [];

  constructor(
    protected http: HttpClient,
    protected ng2ImgToolsService: Ng2ImgMaxService, 
    protected s3Service: S3Service,
    protected imgMaxSizeService: ImgMaxPXSizeService,
    protected ng2PicaService: Ng2PicaService,
    protected sanitizer: DomSanitizer,
    protected jwtTokenService: JWTTokenService) {
    super(http, jwtTokenService);
  }

  public clearQueue() {
    this.tempUrls.forEach(url => URL.revokeObjectURL(url));
    this.resizeObservablesDict = {};
  }

  public commitUploads(): Observable<any> {

    console.log('Commit called, waiting for all resize operations to complete...');

    let completed = 0;

    const locations = Object.keys(this.resizeObservablesDict);

    const uploads$ = locations
      .map(
        loc => {

          const resizeObservable = this.resizeObservablesDict[loc];
          // phase 3: when resizing and commitCalled are both complete, continue with upload
          return resizeObservable
            .pipe(mergeMap(
              res => {

                console.log('Uploading resized file: ' + res.file.name);

                return this.registerUploadedPhoto(res.file, 1, loc)
                  .pipe(mergeMap(photo => this.s3Service.uploadFile(res.file, photo.folderName.toLowerCase().replace(/ /g, '-'), "get-signed-s3-url")))
                  .pipe(map(
                    uploadUrl => {

                      console.log(`Uploded ${++completed}/${locations.length} images`);
                      // once uploaded, provide the permanent url
                      res.subscriber.next({
                        name: res.file.name,
                        url: Environment.s3CdnUrl + this.s3Service.getUrlWithoutSignature(uploadUrl.split('s3.amazonaws.com')[1]),
                        location: loc,
                        stage: ImageProcessingStage.Uploaded
                      });
                    }
                  ));
              }
            ));
      }
    );

    return  (uploads$.length > 0 ? forkJoin(uploads$) : of(null)).pipe(tap(_ => {
      this.clearQueue();
      console.log('Uploading images complete');
    }));
  }

  private resizeFile(imageFile: File, maxSize: { height: number, width: number }, keepAspectRatio?: boolean, resizeExact?: boolean): Observable<File> {

    if (imageFile?.name?.toLowerCase().endsWith('.gif')) {

      if (imageFile.size > 5 * 1024 * 1024) { // return error if over 5mb

        return of(null);

      } else {

        return of(imageFile);
      }
    }
    
    if (maxSize.height === 0 && maxSize.width === 0) {
      
      return of (imageFile);
    }
    
    const arrayBufferObs = from(imageFile.arrayBuffer());

    return arrayBufferObs
      .pipe(mergeMap(
        buffer => {

          const jimpObs = from(Jimp.read(Buffer.from(buffer)));

          return jimpObs
            .pipe(mergeMap(
              image => {

                if (resizeExact) { 

                  // If we want an exact resize, squeeze/stretch/disfigure the image until it fits the dimensions
                  image.resize(maxSize.width, maxSize.height)
            
                } else if (keepAspectRatio) {

                  // If we want to keep the aspect ratio, scale the image as much as possible until we hit a constraint.
                  // The resulting image will have the provided dimensions
                  image.contain(maxSize.width, maxSize.height);

                } else if (maxSize) {

                  // If neither are selected, scale the image to the largest w x h that fits contraints
                  // The resulting image will have w x h that is either equal to or less than maxSize dimensions
                  image.scaleToFit(maxSize.width, maxSize.height);

                }

                return from(image.getBufferAsync(imageFile.type))
                    .pipe(mergeMap(
                      imageBuffer => {

                        return of(new File([imageBuffer], imageFile.name));
                      }
                    ));
              }
            ));
        }
      ));
  }

  /**
   * Enqueues an image file for upload. The actual upload is initiated when commitUploads() is called. In the meantime, a temporary url (to the in-memory file) is provided to the caller.
   * @returns An observable that fires three times: first with a temporary in-memory url with the original image, then with an in-memory url with the resized image, and finally after commitUploads() is called with the permanent url of the resized and uploaded file.
   * @param maxSize If provided, the image will be resized to the specified maximum size before uploading. Aspect ratio is maitained. For gif files, this parameter is ignored; they will be resized to 360x640.
   * @param locationId  An arbitrary object identifying the location where the image will be placed, so that if the user changes their mind several times and uploads several images for the same location, only the last image for that location will be uploaded when calling commitUploads().
   */
  public enqueueUploadPhoto(
    file: File,
    locationId: any,
    maxSize: { height: number; width: number },
    keepAspectRatio?: boolean,
    resizeExact?: boolean
  ): Observable<PhotoQueueEvent> {
    return new Observable<PhotoQueueEvent>(s => {
      // phase 1: provide temp url immediately
      const tempUrl = URL.createObjectURL(file);
      this.tempUrls.push(tempUrl);
      const safeUrl = this.sanitizer.bypassSecurityTrustUrl(tempUrl);
      s.next({ name: file.name, url: <any>safeUrl, location: locationId, stage: ImageProcessingStage.Original });

      // phase 2: resizing
      const resizeObservable: Observable<ImageProcessingInfo> = this.resizeFile(file, maxSize, keepAspectRatio, resizeExact)
        .pipe(map(resizedFile => {
          if (this.resizeObservablesDict[locationId] === resizeObservable) {
            const resizedTempUrl = URL.createObjectURL(resizedFile);
            this.tempUrls.push(resizedTempUrl);
            const resizedSafeUrl = this.sanitizer.bypassSecurityTrustUrl(resizedTempUrl);
            s.next({ name: file.name, url: <any>resizedSafeUrl, location: locationId, stage: ImageProcessingStage.Resized });
            const result = { file: resizedFile, subscriber: s };
            return result;
          }
        }))
        .pipe(shareReplay()); // if resize completes, and then some time later "commit" is called, we want to replay the results rather than execute resize again
      this.resizeObservablesDict[locationId] = resizeObservable; // register the resize observable for this location, so we can wait for it if commit called
      resizeObservable.subscribe( // start resizing immediately
        next => next,
        error => {
          s.error(error);
        });
    });
  }

  public meetsImageConstraints (file: File, width: number, height: number, ratio?: number, ratioError?: number) : Observable<boolean> {

    if (!file.type?.includes('image')) {

      throw new Error("That file is not an image.")
    }

    return new Observable<boolean>(
      subscriber => {

        const reader = new FileReader();
        reader.readAsDataURL(file);

        reader.onload = (e) => {

          const img = new Image();
          img.src = e.target.result as string;

          img.onload = () => {

            let aspectRatio = img.width/img.height;

            const correctDimentions = img.height >= height && img.width >= width;
            let correctRatio = true;

            if (typeof ratio !== 'undefined' && typeof ratioError !== 'undefined') {

              correctRatio = aspectRatio >= ratio - ratioError && aspectRatio <= ratio + ratioError;
            }

            subscriber.next(correctDimentions && correctRatio);
          }
        }
      }
    );
  }

  public exactImageConstraints (file: File, width: number, height: number) : Observable<boolean> {

    if (!file.type.includes('image')) {

      throw new Error("That file is not an image.")
    }

    return new Observable<boolean>(
      subscriber => {

        const reader = new FileReader();
        reader.readAsDataURL(file);

        reader.onload = (e) => {

          const img = new Image();
          img.src = e.target.result as string;

          img.onload = () => {

            subscriber.next(img.width === width && img.height === height);
          }
        }
      }
    );
  }

  protected registerUploadedPhoto(file: File, imageType: number, filePath?: string): Observable<Photo> {

    const lastDotIdx = file.name.lastIndexOf('.');
    const name = file.name.substring(0, lastDotIdx);
    const extension = file.name.substring(lastDotIdx + 1);
    const uniqueName = `${name}_${ Date.now().toString().slice(-4) + Math.floor(1000 + 9000 * Math.random()) }.${extension}`;

    const postUrl = getUrl('assets/photo/add');
    return this.http.post<Photo>(postUrl, { name: uniqueName.replace(/ /g, ''), type: imageType }, this.requestOptionsAuthenticated());
  }

  public getUserPhotos() {
    return this.http.get<Photo[]>(getUrl('assets/getPhotos'), this.requestOptionsAuthenticated());
  }

  public getPhoto(id: number) {
    return this.http.get<Photo>(getUrl('assets/photo/' + id), this.requestOptionsAuthenticated());
  }

  public getSelfieCamPhotos(id: number) {
    return this.http.get<Photo[]>(getUrl(`assets/selfiecam/${id}/photos`), this.requestOptionsAuthenticated());
  }

  public deleteSelfieCamPhoto(id: number) {
    return this.http.delete(getUrl(`assets/selfiecam/photos/${id}`), this.requestOptionsAuthenticated());
  }

  public getAdminImages(): Observable<any> {
    return this.http.get<any>(getUrl('assets/image'), this.requestOptionsAuthenticated());
  }

  public deleteAdminImage(bucketName: string, objectPath: string): Observable<any> {
    const s3Object = { bucketName, objectPath };
    return this.http.post<any>(getUrl('assets/image/delete'), s3Object, this.requestOptionsAuthenticated());
  }

  protected requestOptionsAuthenticated() {
    const requestOptions = {
      headers: new HttpHeaders().set('Content-type', 'application/json').append('Authorization', 'Bearer ' + this.jwtTokenService.getToken('portal'))
    };
    return requestOptions;
  }
  
}

export interface PhotoQueueEvent {
  name: string;
  // the url to display (can be a temp url or a permanent url)
  url: string;
  // the temporary safeUrl that needs to be replaced with the permanent url
  // useful if the photo was displayed in a formarray, enables finding the old value and replacing it with the new one.
  location: string;
  // if true, the url is a temporary url.
  // if false, the url is a permanent url which can replace the old temporary url.
  stage: ImageProcessingStage;
}

interface ImageProcessingInfo {
  file: File;
  subscriber: Subscriber<any>;
}
